// Buffers the browser's `beforeinstallprompt` event so the dashboard can offer
// its own "Install App" button instead of relying on the address-bar icon.
//
// The event is the reason this module exists at all rather than living in the
// Home panel. Chrome fires it once, early, on its own schedule (after the
// ~30s engagement heuristic is met) — routinely BEFORE HomePanel mounts, and
// sometimes before React has rendered at all. A listener attached inside a
// component would simply miss it, with no way to ask the browser to re-fire.
// So the listener is attached at import time from index.js and the event is
// parked here; components subscribe and read whatever has already arrived.
//
// The event is also single-use: prompt() may only be called once per event
// object, after which the browser discards it. That is why promptInstall()
// clears the reference rather than keeping it around for a retry.

let deferredPrompt = null;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.error('pwaInstall listener failed:', err);
    }
  });
}

/** True when the app is already running installed, so nothing should be offered. */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  const mql = window.matchMedia && window.matchMedia('(display-mode: standalone)');
  if (mql && mql.matches) return true;
  // iOS Safari never reports display-mode; it sets this non-standard flag.
  return window.navigator && window.navigator.standalone === true;
}

/**
 * iOS never fires `beforeinstallprompt` — by design, not as a timing quirk —
 * so the only signal available is the user agent.
 */
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ ships a desktop-Mac user agent; touch points give it away.
  return /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
}

export function getDeferredPrompt() {
  return deferredPrompt;
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Shows the browser's native install dialog. Must be called from a user
 * gesture — Chrome rejects prompt() outside one.
 */
export async function promptInstall() {
  const evt = deferredPrompt;
  if (!evt) return { outcome: 'unavailable' };

  // Dropped before awaiting: the event is spent the moment prompt() is called,
  // and a second prompt() on it throws. Clearing now also hides the button
  // immediately rather than leaving a control that would throw on a second tap.
  deferredPrompt = null;
  emit();

  try {
    evt.prompt();
    return await evt.userChoice;
  } catch (err) {
    console.error('Install prompt failed:', err);
    return { outcome: 'error' };
  }
}

/** Attaches the listeners. Safe to call more than once. */
export function initPwaInstall() {
  if (typeof window === 'undefined' || window.__apPwaInstallInit) return;
  window.__apPwaInstallInit = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Suppresses Chrome's own mini-infobar — we trigger the dialog ourselves.
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit();
  });
}

// Test-only hatch: the module-level buffer outlives a test's render, so a
// suite that fires a synthetic event needs a way back to a clean slate.
export function __resetForTests() {
  deferredPrompt = null;
  listeners.clear();
  if (typeof window !== 'undefined') delete window.__apPwaInstallInit;
}
